// @ts-strict-ignore
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Form } from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { FormError } from '@actual-app/components/form-error';
import { InitialFocus } from '@actual-app/components/initial-focus';
import { InlineField } from '@actual-app/components/inline-field';
import { Input } from '@actual-app/components/input';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { amountToInteger, toRelaxedNumber } from '@actual-app/core/shared/util';
import type { AccountKind } from '@actual-app/core/types/models';

import { useCreateAccountMutation } from '#accounts';
import { Link } from '#components/common/Link';
import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
} from '#components/common/Modal';
import { Checkbox } from '#components/forms';
import { validateAccountName } from '#components/util/accountValidation';
import { useAccounts } from '#hooks/useAccounts';
import { useNavigate } from '#hooks/useNavigate';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';
import { closeModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

export function CreateLocalAccountModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isUsingServer = useSyncServerStatus() !== 'no-server';
  const { data: accounts = [] } = useAccounts();
  const [name, setName] = useState('');
  const [offbudget, setOffbudget] = useState(false);
  const [balance, setBalance] = useState('0');
  const [kind, setKind] = useState<AccountKind>('standard');
  const [closingDay, setClosingDay] = useState('');
  const [dueDay, setDueDay] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const isCard = kind === 'credit_card';

  const [nameError, setNameError] = useState(null);
  const [balanceError, setBalanceError] = useState(false);

  const validateBalance = balance => !isNaN(parseFloat(balance));
  const parseDay = (value: string) => {
    const day = parseInt(value, 10);
    return Number.isInteger(day) && day >= 1 && day <= 31 ? day : null;
  };
  const daysError =
    isCard &&
    ((closingDay.trim() !== '' && parseDay(closingDay) == null) ||
      (dueDay.trim() !== '' && parseDay(dueDay) == null));

  const validateAndSetName = (name: string) => {
    const nameError = validateAccountName(name, '', accounts);
    if (nameError) {
      setNameError(nameError);
    } else {
      setName(name);
      setNameError(null);
    }
  };

  const createAccount = useCreateAccountMutation();

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nameError = validateAccountName(name, '', accounts);

    const balanceError = !validateBalance(balance);
    setBalanceError(balanceError);

    if (!nameError && !balanceError && !daysError) {
      const amount = toRelaxedNumber(balance);
      createAccount.mutate(
        {
          name,
          // A card's balance is entered as the amount owed.
          balance: isCard ? -Math.abs(amount) : amount,
          offBudget: offbudget,
          kind,
          card: isCard
            ? {
                closingDay: parseDay(closingDay),
                dueDay: parseDay(dueDay),
                creditLimit:
                  creditLimit.trim() === ''
                    ? null
                    : amountToInteger(toRelaxedNumber(creditLimit)),
              }
            : undefined,
        },
        {
          onSuccess: id => {
            dispatch(closeModal());
            void navigate('/accounts/' + id);
          },
        },
      );
    }
  };
  return (
    <Modal name="add-local-account">
      {({ state }) => (
        <>
          <ModalHeader
            title={
              <ModalTitle
                title={
                  isUsingServer ? t('Create Local Account') : t('Add account')
                }
                shrinkOnOverflow
              />
            }
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />
          <View>
            {!isUsingServer && (
              <Text
                style={{
                  color: theme.pageTextSubdued,
                  lineHeight: 1.5,
                  marginBottom: 15,
                }}
              >
                <Trans>
                  Once the account is created, you can also{' '}
                  <Link
                    variant="external"
                    linkColor="muted"
                    to="https://actualbudget.org/docs/transactions/importing"
                  >
                    import QIF/OFX/QFX files
                  </Link>{' '}
                  into it.
                </Trans>
              </Text>
            )}
            <Form onSubmit={onSubmit}>
              <InlineField label={t('Name')} width="100%">
                <InitialFocus>
                  <Input
                    name="name"
                    value={name}
                    placeholder={t('e.g. Bank, Savings, Credit Card, Cash')}
                    onChangeValue={setName}
                    onUpdate={value => {
                      const name = value.trim();
                      validateAndSetName(name);
                    }}
                    style={{ flex: 1 }}
                  />
                </InitialFocus>
              </InlineField>
              {nameError && (
                <FormError style={{ marginLeft: 75, color: theme.warningText }}>
                  {nameError}
                </FormError>
              )}

              <InlineField label={t('Type')} width="100%">
                <Select<AccountKind>
                  value={kind}
                  onChange={setKind}
                  options={[
                    ['standard', t('Account')],
                    ['credit_card', t('Credit card')],
                  ]}
                  style={{ flex: 1 }}
                />
              </InlineField>

              {isCard && (
                <>
                  <InlineField label={t('Closes on')} width="100%">
                    <Input
                      name="closingDay"
                      inputMode="numeric"
                      placeholder={t('Day of the month')}
                      value={closingDay}
                      onChangeValue={setClosingDay}
                      style={{ flex: 1 }}
                    />
                  </InlineField>
                  <InlineField label={t('Due on')} width="100%">
                    <Input
                      name="dueDay"
                      inputMode="numeric"
                      placeholder={t('Day of the month')}
                      value={dueDay}
                      onChangeValue={setDueDay}
                      style={{ flex: 1 }}
                    />
                  </InlineField>
                  <InlineField label={t('Limit')} width="100%">
                    <Input
                      name="creditLimit"
                      inputMode="decimal"
                      value={creditLimit}
                      onChangeValue={setCreditLimit}
                      style={{ flex: 1 }}
                    />
                  </InlineField>
                  {daysError && (
                    <FormError style={{ marginLeft: 75 }}>
                      <Trans>Days must be numbers from 1 to 31</Trans>
                    </FormError>
                  )}
                  <Text
                    style={{
                      color: theme.pageTextLight,
                      fontSize: '0.8em',
                      marginLeft: 75,
                      marginBottom: 10,
                    }}
                  >
                    <Trans>
                      Leave the days empty when the card is linked to a bank:
                      they are read from the bank.
                    </Trans>
                  </Text>
                </>
              )}

              <View
                style={{
                  width: '100%',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                }}
              >
                <View style={{ flexDirection: 'column' }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Checkbox
                      id="offbudget"
                      name="offbudget"
                      checked={offbudget}
                      onChange={() => setOffbudget(!offbudget)}
                    />
                    <label
                      htmlFor="offbudget"
                      style={{
                        userSelect: 'none',
                        verticalAlign: 'center',
                      }}
                    >
                      <Trans>Off budget</Trans>
                    </label>
                  </View>
                  <div
                    style={{
                      textAlign: 'right',
                      fontSize: '0.7em',
                      color: theme.pageTextLight,
                      marginTop: 3,
                    }}
                  >
                    <Text style={{ display: 'block' }}>
                      <Trans>
                        Off-budget accounts (like investments, loans, or your
                        house) are tracked but not part of your spending budget.
                      </Trans>
                    </Text>
                    <Text style={{ display: 'block' }}>
                      <Trans>
                        This cannot be changed later. See{' '}
                        <Link
                          variant="external"
                          linkColor="muted"
                          to="https://actualbudget.org/docs/accounts/#off-budget-accounts"
                        >
                          Accounts Overview
                        </Link>{' '}
                        for more information.
                      </Trans>
                    </Text>
                  </div>
                </View>
              </View>

              <InlineField
                label={isCard ? t('Amount owed') : t('Balance')}
                width="100%"
              >
                <Input
                  name="balance"
                  inputMode="decimal"
                  value={balance}
                  onChangeValue={setBalance}
                  onUpdate={value => {
                    const balance = value.trim();
                    setBalance(balance);
                    if (validateBalance(balance) && balanceError) {
                      setBalanceError(false);
                    }
                  }}
                  style={{ flex: 1 }}
                />
              </InlineField>
              {balanceError && (
                <FormError style={{ marginLeft: 75 }}>
                  <Trans>Balance must be a number</Trans>
                </FormError>
              )}

              <ModalButtons>
                <Button onPress={() => state.close()}>
                  {isUsingServer ? <Trans>Back</Trans> : <Trans>Cancel</Trans>}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  style={{ marginLeft: 10 }}
                >
                  <Trans>Create</Trans>
                </Button>
              </ModalButtons>
            </Form>
          </View>
        </>
      )}
    </Modal>
  );
}
